import type { FamilyConfig } from '@familyjs/core/components';
import { initialize } from '@familyjs/core/components';

export {
  // UTILS
  createAnimation,
  createGesture,
  iosTransitionAnimation,
  mdTransitionAnimation,
  FamilySlides,
  getTimeGivenProgression,
  getFmlPageElement,
  openURL,

  // TYPES
  Animation,
  AnimationBuilder,
  AnimationCallbackOptions,
  AnimationDirection,
  AnimationFill,
  AnimationKeyFrames,
  AnimationLifecycle,
  Gesture,
  GestureConfig,
  GestureDetail,
  NavComponentWithProps,
  SpinnerTypes,
  AccordionGroupCustomEvent,
  AccordionGroupChangeEventDetail,
  BreadcrumbCustomEvent,
  BreadcrumbCollapsedClickEventDetail,
  ActionSheetOptions,
  ActionSheetButton,
  AlertOptions,
  AlertInput,
  AlertButton,
  BackButtonEvent,
  CheckboxCustomEvent,
  CheckboxChangeEventDetail,
  DatetimeCustomEvent,
  DatetimeChangeEventDetail,
  InfiniteScrollCustomEvent,
  InputCustomEvent,
  InputChangeEventDetail,
  ItemReorderEventDetail,
  ItemReorderCustomEvent,
  ItemSlidingCustomEvent,
  FamilySafeString,
  LoadingOptions,
  MenuCustomEvent,
  ModalOptions,
  NavCustomEvent,
  PickerOptions,
  PickerButton,
  PickerColumn,
  PickerColumnOption,
  PopoverOptions,
  RadioGroupCustomEvent,
  RadioGroupChangeEventDetail,
  RangeCustomEvent,
  RangeChangeEventDetail,
  RangeKnobMoveStartEventDetail,
  RangeKnobMoveEndEventDetail,
  RefresherCustomEvent,
  RefresherEventDetail,
  RouterEventDetail,
  RouterCustomEvent,
  ScrollBaseCustomEvent,
  ScrollBaseDetail,
  ScrollDetail,
  ScrollCustomEvent,
  SearchbarCustomEvent,
  SearchbarChangeEventDetail,
  SearchbarInputEventDetail,
  SegmentChangeEventDetail,
  SegmentCustomEvent,
  SegmentValue,
  SelectChangeEventDetail,
  SelectCustomEvent,
  TabsCustomEvent,
  TextareaChangeEventDetail,
  TextareaCustomEvent,
  ToastOptions,
  ToastButton,
  ToastLayout,
  ToggleChangeEventDetail,
  ToggleCustomEvent,
  TransitionOptions,
} from '@familyjs/core/components';

export * from './proxies';
export * from './routing-proxies';

// createControllerComponent
export { FmlAlert } from './FmlAlert';
export { FmlLoading } from './FmlLoading';
export * from './FmlToast';
export { FmlPickerLegacy } from './FmlPickerLegacy';

// createOverlayComponent
export * from './FmlActionSheet';
export { FmlModal } from './FmlModal';
export { FmlPopover } from './FmlPopover';

// Custom Components
export { FmlApp } from './FmlApp';
export { FmlPage } from './FmlPage';
export { FmlNav } from './navigation/FmlNav';
export { FmlTabsContext, FmlTabsContextState } from './navigation/FmlTabsContext';
export { FmlTabs } from './navigation/FmlTabs';
export { FmlTabBar } from './navigation/FmlTabBar';
export { FmlTabButton } from './navigation/FmlTabButton';
export { FmlBackButton } from './navigation/FmlBackButton';
export { FmlRouterOutlet } from './FmlRouterOutlet';
export { FmlIcon } from './FmlIcon';
export * from './FmlRoute';
export * from './FmlRedirect';
export * from './FmlRouterContext';

// Utils
export { isPlatform, getPlatforms, getConfig } from './utils';
export * from './hrefprops';

// Family Animations
export { CreateAnimation } from './CreateAnimation';

// Hooks
export { useFmlActionSheet, UseFmlActionSheetResult } from '../hooks/useFmlActionSheet';
export { useFmlAlert, UseFmlAlertResult } from '../hooks/useFmlAlert';
export { useFmlToast, UseFmlToastResult } from '../hooks/useFmlToast';
export { useFmlModal, UseFmlModalResult } from '../hooks/useFmlModal';
export { useFmlPopover, UseFmlPopoverResult } from '../hooks/useFmlPopover';
export { useFmlPicker, UseFmlPickerResult } from '../hooks/useFmlPicker';
export { useFmlLoading, UseFmlLoadingResult } from '../hooks/useFmlLoading';

export const setupFamilyReact = (config: FamilyConfig = {}) => {
  /**
   * By default Family Framework hides elements that
   * are not hydrated, but in the CE build there is no
   * hydration.
   * TODO FW-2797: Remove when all integrations have been
   * migrated to CE build.
   */
  if (typeof (document as any) !== 'undefined') {
    document.documentElement.classList.add('fml-ce');
  }

  initialize({
    ...config,
  });
};
